import axios from 'axios'
import { useAccessGuardStore } from '@/store'

// Base API URL from environment variables
export const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL || 'https://dev.api.ogaboss.io/api/v1'

// Create axios instance
export const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 seconds
})

// Request interceptor - Add auth token to requests
axiosInstance.interceptors.request.use(
    config => {
        // Get token from localStorage
        const authStore = localStorage.getItem('auth-store')
        if (authStore) {
            try {
                const { state } = JSON.parse(authStore)
                const token = state?.token

                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
            } catch (error) {
                console.error('Error parsing auth store:', error)
            }
        }

        return config
    },
    error => {
        return Promise.reject(error)
    }
)

/** Drop credentials and send the user to sign-in, unless already on an auth page. */
function clearAuthAndRedirect() {
    if (window.location.pathname.startsWith('/auth')) return
    localStorage.removeItem('auth-store')
    window.location.href = '/auth/sign-in'
}

// Response interceptor - Handle token refresh and errors
axiosInstance.interceptors.response.use(
    response => {
        return response
    },
    async error => {
        const originalRequest = error.config as typeof error.config & {
            _retry?: boolean
        }

        if (error.response?.status === 403) {
            const data = error.response.data as {
                message?: string
                path?: string
                errors?: string[]
            }
            const accessMessage =
                (typeof data?.message === 'string' && data.message) ||
                (Array.isArray(data?.errors) && data.errors[0]) ||
                undefined

            useAccessGuardStore.getState().setAccessDenied({
                statusCode: error.response.status,
                message: accessMessage,
                path:
                    (typeof data?.path === 'string' && data.path) ||
                    originalRequest?.url,
            })
        }

        // Handle 401 Unauthorized
        if (
            error.response?.status === 401 &&
            originalRequest &&
            !originalRequest._retry
        ) {
            // Only an expired token is worth refreshing. A revoked session or a
            // malformed token would refresh successfully and then fail again on
            // retry, so those go straight to logout.
            const code = (error.response.data as { code?: string })?.code
            if (code && code !== 'TOKEN_EXPIRED') {
                clearAuthAndRedirect()
                return Promise.reject(error)
            }

            originalRequest._retry = true

            try {
                // Get refresh token from localStorage
                const authStore = localStorage.getItem('auth-store')
                if (authStore) {
                    const { state } = JSON.parse(authStore)
                    const refreshToken = state?.refreshToken

                    if (refreshToken) {
                        // Refresh lives on the shared auth route, not under
                        // /admin — there has never been an /auth/admin/refresh-token
                        // endpoint, so this call used to 404 and log admins out.
                        // refreshAccessToken() branches on userType internally
                        // and issues an admin-scoped token.
                        const response = await axios.post(
                            `${API_BASE_URL}/auth/refresh-token`,
                            { refreshToken }
                        )

                        if (response.data?.data?.accessToken) {
                            const newToken = response.data.data.accessToken

                            // Update token in localStorage
                            const updatedStore = {
                                ...JSON.parse(authStore),
                                state: {
                                    ...JSON.parse(authStore).state,
                                    token: newToken,
                                },
                            }
                            localStorage.setItem(
                                'auth-store',
                                JSON.stringify(updatedStore)
                            )

                            // Retry original request with new token
                            originalRequest.headers.Authorization = `Bearer ${newToken}`
                            return axiosInstance(originalRequest)
                        }
                    }
                }

                // If refresh fails, clear auth and redirect to login
                clearAuthAndRedirect()
            } catch (refreshError) {
                clearAuthAndRedirect()
                return Promise.reject(refreshError)
            }
        }

        return Promise.reject(error)
    }
)

export default axiosInstance
